import type { GameEvent } from '../schema';

/**
 * Content expansion.
 *
 * Every pool the board can draw from needs enough material that a player never
 * sees the same card twice in a decade. These events fill out the pools the
 * validator flagged as thin: early career, health at each stage, caring for
 * parents, teenagers, burnout, career pivots, late career, and retirement.
 */
export const EXPANSION_EVENTS: GameEvent[] = [
  /* ---------------- early career entry ---------------- */
  {
    id: 'entry.internship',
    title: 'An unpaid foot in the door',
    description:
      'Six months of real experience at a real employer, for expenses only. Someone else would take it tomorrow.',
    category: 'career',
    pools: ['pool.career.entry', 'pool.young'],
    ageMax: 30,
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'careerStatus', anyOf: ['none', 'student', 'unemployed'] }],
    choices: [
      {
        id: 'take',
        label: 'Take it',
        detail: 'Live on nothing. Build the CV.',
        hint: 'costly',
        tags: ['career', 'patience'],
        cost: [{ k: 'cashIncomeMonths', months: -1.4, note: 'unpaid internship' }],
        outcomes: [
          {
            weight: 62,
            label: 'It converted',
            narrative: 'They kept you on. The six months of poverty bought two years of seniority.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'skill', skill: 'social', op: '>=', value: 45 }], multiplier: 1.5 }],
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'performance', delta: 8 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 6 },
              { k: 'skill', skill: 'communication', delta: 5 },
            ],
          },
          {
            weight: 38,
            label: 'A line on the CV',
            narrative: 'Nothing came of it directly. It did make the next application land.',
            tone: 'neutral',
            effects: [
              { k: 'skill', skill: 'communication', delta: 4 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 3 },
            ],
          },
        ],
      },
      {
        id: 'paid',
        label: 'Insist on being paid',
        detail: 'Take a worse job that actually pays.',
        hint: 'safe',
        tags: ['pragmatic', 'boundaries'],
        outcomes: [
          {
            weight: 1,
            label: 'Took a paid job instead',
            narrative: 'Less prestigious, immediately solvent. Your landlord approved.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -5 },
              { k: 'stat', stat: 'stress', delta: -5 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'entry.familyBusiness',
    title: 'There is a place for you at home',
    description: 'A family business, an assured job, and a ceiling you can see from the door.',
    category: 'career',
    pools: ['pool.career.entry'],
    ageMax: 32,
    weight: 5,
    unique: true,
    requires: [{ k: 'careerStatus', anyOf: ['none', 'unemployed'] }, { k: 'hasPerson', kind: 'parent' }],
    choices: [
      {
        id: 'join',
        label: 'Join them',
        hint: 'safe',
        tags: ['family', 'security'],
        outcomes: [
          {
            weight: 1,
            label: 'Joined the family business',
            narrative: 'Secure, uncomplicated, and permanently entangled with your relatives.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: 22 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -8 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 12 },
              { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: 10 },
              { k: 'skill', skill: 'business', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'decline',
        label: 'Make your own way',
        hint: 'mixed',
        tags: ['independence'],
        outcomes: [
          {
            weight: 1,
            label: 'Went your own way',
            narrative: 'They said they understood. Your mother said it twice.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: -8 },
              { k: 'trait', trait: 'ambition', delta: 5 },
              { k: 'stat', stat: 'purpose', delta: 5 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'entry.firstReview',
    title: 'Your first performance review',
    description: 'Someone writes down what they think of you. It is mostly fair, which is the uncomfortable part.',
    category: 'career',
    pools: ['pool.career.entry', 'pool.career.early'],
    weight: 6,
    cooldown: 8,
    requires: [{ k: 'careerStatus', anyOf: ['employed'] }, { k: 'num', path: 'yearsExperience', op: '>=', value: 1 }],
    choices: [
      {
        id: 'act',
        label: 'Take the criticism seriously',
        hint: 'safe',
        tags: ['growth'],
        outcomes: [
          {
            weight: 1,
            label: 'Acted on it',
            narrative: 'You fixed the two things they named. It was noticed within a year.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: 9 },
              { k: 'skill', skill: 'selfManagement', delta: 6 },
              { k: 'trait', trait: 'conscientiousness', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'dismiss',
        label: 'Decide they are wrong',
        hint: 'risky',
        tags: ['denial'],
        outcomes: [
          {
            weight: 1,
            label: 'Dismissed it',
            narrative: 'Possibly they were wrong. The feedback recurred, in the same words, for years.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: -5 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: -3 },
              { k: 'trait', trait: 'adaptability', delta: -3 },
            ],
          },
        ],
      },
    ],
  },

  /* ---------------- health, early ---------------- */
  {
    id: 'health.earlyInvincible',
    title: 'Nothing has consequences yet',
    description: 'You can survive on four hours and a takeaway. This will not always be true.',
    category: 'health',
    pools: ['pool.health.early', 'pool.young'],
    ageMax: 32,
    weight: 7,
    cooldown: 5,
    choices: [
      {
        id: 'bank',
        label: 'Build the habits now anyway',
        detail: 'Boring, and the cheapest health you will ever buy.',
        hint: 'slow',
        tags: ['health', 'discipline'],
        outcomes: [
          {
            weight: 1,
            label: 'Started early',
            narrative: 'You will not be able to point to a moment when this paid off. It will have paid off.',
            tone: 'positive',
            effects: [
              { k: 'habit', habit: 'exercise', delta: 14 },
              { k: 'habit', habit: 'sleep', delta: 12 },
              { k: 'stat', stat: 'health', delta: 5 },
              {
                k: 'schedule',
                afterYears: 20,
                label: 'Twenty years of looking after yourself',
                requires: [{ k: 'habit', habit: 'exercise', op: '>=', value: 45 }],
                narrative: 'The habits you built at twenty-five are still working.',
                effects: [{ k: 'stat', stat: 'health', delta: 10 }, { k: 'stat', stat: 'energy', delta: 8 }],
              },
            ],
          },
        ],
      },
      {
        id: 'enjoy',
        label: 'Enjoy the immunity while it lasts',
        hint: 'risky',
        tags: ['lifestyle'],
        outcomes: [
          {
            weight: 1,
            label: 'Lived like it was free',
            narrative: 'It genuinely was free, for about fifteen years.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'habit', habit: 'exercise', delta: -10 },
              { k: 'habit', habit: 'sleep', delta: -10 },
              {
                k: 'schedule',
                afterYears: 16,
                label: 'The invoice for your twenties',
                requires: [{ k: 'habit', habit: 'exercise', op: '<=', value: 45 }],
                effects: [{ k: 'stat', stat: 'health', delta: -12 }, { k: 'stat', stat: 'energy', delta: -8 }],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.earlyInjury',
    title: 'You have hurt something',
    description: 'A knee, a back, a shoulder. It will heal properly if you let it, and badly if you do not.',
    category: 'health',
    pools: ['pool.health.early', 'pool.health.mid'],
    weight: 6,
    cooldown: 8,
    choices: [
      {
        id: 'rehab',
        label: 'Rehabilitate it properly',
        hint: 'costly',
        tags: ['health', 'patience'],
        outcomes: [
          {
            weight: 1,
            label: 'Healed',
            narrative: 'Four months of unglamorous exercises. It stopped being a problem.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -0.5, note: 'physiotherapy' },
              { k: 'stat', stat: 'health', delta: 4 },
              { k: 'habit', habit: 'exercise', delta: 6 },
            ],
          },
        ],
      },
      {
        id: 'ignore',
        label: 'Work through it',
        hint: 'risky',
        tags: ['denial'],
        outcomes: [
          {
            weight: 44,
            label: 'It settled',
            narrative: 'It faded on its own, mostly.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'health', delta: -2 }],
          },
          {
            weight: 56,
            label: 'It became permanent',
            narrative: 'It never fully went away. You learned which movements to avoid.',
            tone: 'negative',
            effects: [
              { k: 'health', op: 'condition', id: 'cond.oldInjury', label: 'Long-term injury', severity: 26 },
              { k: 'habit', habit: 'exercise', delta: -12 },
              { k: 'stat', stat: 'health', delta: -6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.mentalHealth',
    title: 'Something is off, and it is not your body',
    description: 'Flat mornings, short temper, and a persistent sense of being slightly underwater.',
    category: 'health',
    pools: ['pool.health.early', 'pool.health.mid', 'pool.stress'],
    weight: 7,
    cooldown: 7,
    weightMods: [
      { conditions: [{ k: 'num', path: 'stress', op: '>=', value: 60 }], multiplier: 1.9 },
      { conditions: [{ k: 'num', path: 'happiness', op: '<=', value: 40 }], multiplier: 1.8 },
    ],
    choices: [
      {
        id: 'help',
        label: 'Get actual help',
        hint: 'costly',
        tags: ['health', 'courage'],
        outcomes: [
          {
            weight: 74,
            label: 'It helped',
            narrative: 'A year of appointments and a set of tools you will use for the rest of your life.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -0.9, note: 'therapy' },
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'stat', stat: 'stress', delta: -16 },
              { k: 'burnout', op: 'load', delta: -14 },
              { k: 'trait', trait: 'resilience', delta: 6 },
              { k: 'skill', skill: 'selfManagement', delta: 7 },
            ],
          },
          {
            weight: 26,
            label: 'The wrong fit',
            narrative: 'The first person you saw was not right for you. Most people stop there.',
            tone: 'neutral',
            effects: [
              { k: 'cashIncomeMonths', months: -0.5, note: 'therapy' },
              { k: 'stat', stat: 'happiness', delta: 3 },
              { k: 'intention', op: 'add', id: 'int.mentalHealth', label: 'Try again with someone else', topic: 'health', urgency: 35 },
            ],
          },
        ],
      },
      {
        id: 'alone',
        label: 'Manage it yourself',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Handled it privately',
            narrative: 'You got functional again. Nobody was told, which made the next time harder.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'burnout', op: 'load', delta: 8 },
              { k: 'relationshipAll', dim: 'closeness', delta: -4 },
              { k: 'intention', op: 'add', id: 'int.mentalHealth', label: 'Talk to someone about it', topic: 'health', urgency: 45 },
            ],
          },
        ],
      },
      {
        id: 'lean',
        label: 'Tell the people close to you',
        hint: 'safe',
        tags: ['support', 'relationship'],
        outcomes: [
          {
            weight: 1,
            label: 'Let people in',
            narrative: 'Not a cure. It made the difficult year a shared one.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'stat', stat: 'stress', delta: -9 },
              { k: 'relationshipAll', dim: 'trust', delta: 8 },
              { k: 'relationshipAll', dim: 'support', delta: 9 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.oldAgeFall',
    title: 'A fall',
    description: 'Nothing broken, this time. It reframes every staircase in the house.',
    category: 'health',
    pools: ['pool.health.old'],
    weight: 8,
    cooldown: 4,
    requires: [{ k: 'age', min: 68 }],
    choices: [
      {
        id: 'adapt',
        label: 'Adapt the house and the routine',
        hint: 'safe',
        tags: ['health', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Made changes',
            narrative: 'Rails, better lighting, and a standing arrangement with a neighbour.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -0.8, note: 'home adaptations' },
              { k: 'stat', stat: 'health', delta: 5 },
              { k: 'stat', stat: 'stress', delta: -6 },
              { k: 'relationshipAll', kind: 'friend', dim: 'support', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'deny',
        label: 'Tell nobody',
        hint: 'risky',
        tags: ['denial', 'independence'],
        outcomes: [
          {
            weight: 45,
            label: 'Got away with it',
            narrative: 'You were more careful and nothing happened.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'happiness', delta: 3 }],
          },
          {
            weight: 55,
            label: 'A worse one followed',
            narrative: 'The second fall broke something, and the recovery took the best part of a year.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'health', delta: -16 },
              { k: 'health', op: 'condition', id: 'cond.mobility', label: 'Reduced mobility', severity: 46 },
              { k: 'cashIncomeMonths', months: -2, note: 'care costs' },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'health.oldAgeCare',
    title: 'Living alone is getting harder',
    description: 'Not impossible. Harder. The question is who decides, and when.',
    category: 'health',
    pools: ['pool.health.old'],
    weight: 7,
    cooldown: 5,
    requires: [{ k: 'age', min: 74 }],
    choices: [
      {
        id: 'support',
        label: 'Accept help at home',
        hint: 'safe',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Accepted help',
            narrative: 'Someone comes three mornings a week. You kept your house and most of your autonomy.',
            tone: 'positive',
            effects: [
              { k: 'fixedExpenses', mode: 'add', value: 11000 },
              { k: 'stat', stat: 'health', delta: 6 },
              { k: 'stat', stat: 'happiness', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'family',
        label: 'Move in with family',
        hint: 'mixed',
        requires: [{ k: 'hasPerson', kind: 'child' }],
        showWhenLocked: true,
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'Moved in with your children',
            narrative: 'Three generations in one house. Warmer, louder, and occasionally humiliating.',
            tone: 'neutral',
            effects: [
              { k: 'fixedExpenses', mode: 'mult', value: 0.45 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 12 },
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 10 },
              { k: 'stat', stat: 'happiness', delta: 7 },
              { k: 'stat', stat: 'health', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'alone',
        label: 'Stay independent',
        hint: 'risky',
        tags: ['independence'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed put',
            narrative: 'Your house, your rules, your risk. You would not have had it otherwise.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'stat', stat: 'health', delta: -6 },
              { k: 'stat', stat: 'purpose', delta: 4 },
            ],
          },
        ],
      },
    ],
  },

  /* ---------------- parents and teenagers ---------------- */
  {
    id: 'parents.roleReversal',
    title: 'You are the adult now',
    description: 'Your parents ask your advice on things they used to decide. It happened gradually and then all at once.',
    category: 'family',
    pools: ['pool.family.parents'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'hasPerson', kind: 'parent' }, { k: 'age', min: 34 }],
    choices: [
      {
        id: 'step-up',
        label: 'Take it on',
        hint: 'mixed',
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'Became the one who handles things',
            narrative: 'Insurance, appointments, the difficult conversations. Nobody thanks you for this.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 16 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'stat', stat: 'energy', delta: -6 },
              { k: 'skill', skill: 'selfManagement', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'boundaries',
        label: 'Help, within limits',
        hint: 'safe',
        tags: ['boundaries'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept limits',
            narrative: 'You helped with the important things and declined the rest. Sustainable, and slightly cold.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 6 },
              { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: 6 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'parents.oldGrievance',
    title: 'The thing you never discussed',
    description: 'Something from your childhood that has sat between you for thirty years. They are getting older.',
    category: 'family',
    pools: ['pool.family.parents', 'pool.legacy'],
    weight: 6,
    unique: true,
    requires: [{ k: 'hasPerson', kind: 'parent' }, { k: 'age', min: 38 }],
    choices: [
      {
        id: 'raise',
        label: 'Raise it',
        hint: 'risky',
        tags: ['courage', 'family'],
        outcomes: [
          {
            weight: 48,
            label: 'They heard it',
            narrative: 'An apology thirty years late, which turned out to still count.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 55 }], multiplier: 1.7 }],
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'trust', delta: 24 },
              { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: -24 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'regret', op: 'resolve', id: 'regret.familyMoney' },
            ],
          },
          {
            weight: 52,
            label: 'They could not',
            narrative: 'They denied it, then changed the subject. You had at least said it out loud.',
            tone: 'negative',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'conflict', delta: 14 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'trait', trait: 'resilience', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'leave',
        label: 'Leave it alone',
        hint: 'safe',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Left it',
            narrative: 'Perhaps kinder. It stayed exactly where it was.',
            tone: 'neutral',
            effects: [
              { k: 'regret', op: 'add', id: 'regret.unsaid', label: 'Never said the thing to your parents', weight: 14 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'teen.independence',
    title: 'They want to make their own mistakes',
    description: 'A decision you can see going badly, and a person who has every right to make it.',
    category: 'family',
    pools: ['pool.family.teen'],
    weight: 8,
    cooldown: 4,
    requires: [{ k: 'hasPerson', kind: 'child' }, { k: 'age', min: 35 }],
    choices: [
      {
        id: 'allow',
        label: 'Let them',
        hint: 'mixed',
        tags: ['family', 'patience'],
        outcomes: [
          {
            weight: 58,
            label: 'They learned something',
            narrative: 'It went badly, they recovered, and they came to you afterwards. That last part is the win.',
            tone: 'positive',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 18 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 8 },
              { k: 'stat', stat: 'stress', delta: 6 },
            ],
          },
          {
            weight: 42,
            label: 'It cost more than expected',
            narrative: 'You were right, which helped nobody.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -1.2, note: 'sorting it out' },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 8 },
              { k: 'stat', stat: 'stress', delta: 10 },
            ],
          },
        ],
      },
      {
        id: 'intervene',
        label: 'Intervene',
        hint: 'risky',
        tags: ['family', 'control'],
        outcomes: [
          {
            weight: 40,
            label: 'You prevented it',
            narrative: 'The bad outcome did not happen. They have not forgotten that you decided.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 16 },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: -8 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
          {
            weight: 60,
            label: 'They did it anyway',
            narrative: 'They did it without telling you, which was strictly worse than the original plan.',
            tone: 'negative',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: -16 },
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 18 },
              { k: 'family', op: 'adjustHarmony', delta: -10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'teen.tuition',
    title: 'Their education costs money',
    description: 'You can fund it, borrow for it, or tell them to fund it themselves. All three are defensible.',
    category: 'family',
    pools: ['pool.family.teen', 'pool.finance.mid'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'hasPerson', kind: 'child' }, { k: 'age', min: 38 }],
    choices: [
      {
        id: 'fund',
        label: 'Pay for it',
        hint: 'costly',
        requires: [{ k: 'num', path: 'netWorth', op: '>=', value: 40000 }],
        showWhenLocked: true,
        tags: ['family', 'generosity'],
        outcomes: [
          {
            weight: 1,
            label: 'Funded them',
            narrative: 'They graduated without debt, which quietly changed the next fifteen years of their life.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -9, note: 'tuition' },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 14 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'counter', name: 'contributions', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'split',
        label: 'Split it with them',
        hint: 'safe',
        tags: ['family', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Shared the cost',
            narrative: 'They had skin in the game and you kept your retirement plan.',
            tone: 'positive',
            effects: [
              { k: 'cashIncomeMonths', months: -3.5, note: 'tuition share' },
              { k: 'relationshipAll', kind: 'child', dim: 'trust', delta: 8 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 4 },
            ],
          },
        ],
      },
      {
        id: 'theirs',
        label: 'Their cost to carry',
        hint: 'mixed',
        tags: ['boundaries'],
        outcomes: [
          {
            weight: 1,
            label: 'They borrowed',
            narrative: 'They managed. They also mention the loan at family gatherings.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 12 },
              { k: 'habit', habit: 'saving', delta: 5 },
            ],
          },
        ],
      },
    ],
  },

  /* ---------------- burnout pool ---------------- */
  {
    id: 'burnout.cynicism',
    title: 'You have stopped caring',
    description:
      'Not tired — indifferent. The work you used to defend now strikes you as faintly ridiculous.',
    category: 'wellbeing',
    pools: ['pool.burnout', 'pool.stress'],
    weight: 7,
    cooldown: 5,
    requires: [{ k: 'burnout', anyOf: ['exhausted', 'atRisk', 'overextended'] }],
    choices: [
      {
        id: 'reduce',
        label: 'Take the load off now',
        hint: 'costly',
        tags: ['rest', 'recovery'],
        outcomes: [
          {
            weight: 1,
            label: 'Cut back',
            narrative: 'You dropped two commitments and stopped answering the evening messages.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -18 },
              { k: 'burnout', op: 'load', delta: -20 },
              { k: 'stat', stat: 'stress', delta: -14 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -5 },
            ],
          },
        ],
      },
      {
        id: 'meaning',
        label: 'Find a reason to care again',
        hint: 'mixed',
        tags: ['purpose'],
        outcomes: [
          {
            weight: 46,
            label: 'You reconnected with it',
            narrative: 'One project, one person, one reason. It was enough to change the year.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'meaningAlignment', op: '>=', value: 50 }], multiplier: 1.8 }],
            effects: [
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 16 },
              { k: 'stat', stat: 'purpose', delta: 12 },
              { k: 'burnout', op: 'load', delta: -12 },
            ],
          },
          {
            weight: 54,
            label: 'There was nothing there',
            narrative: 'You looked honestly and found no reason. That is information, not failure.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: -12 },
              { k: 'burnout', op: 'load', delta: 8 },
              { k: 'intention', op: 'add', id: 'int.jobSearch', label: 'Leave this job', topic: 'career', urgency: 45, resumeEventId: 'career.jobSearch', decayEventId: 'career.staleCareer' },
            ],
          },
        ],
      },
      {
        id: 'coast',
        label: 'Do the minimum and say nothing',
        hint: 'risky',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Quietly disengaged',
            narrative: 'Nobody noticed for two years. You noticed every day.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'performance', delta: -12 },
              { k: 'stat', stat: 'purpose', delta: -10 },
              { k: 'burnout', op: 'load', delta: 6 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -10 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'burnout.bodyTalking',
    title: 'Your body is making the argument',
    description: 'Headaches, a stomach that objects to everything, and sleep that stopped working.',
    category: 'wellbeing',
    pools: ['pool.burnout', 'pool.health.mid'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 55 }],
    choices: [
      {
        id: 'listen',
        label: 'Treat it as non-negotiable',
        hint: 'costly',
        tags: ['health', 'recovery'],
        outcomes: [
          {
            weight: 1,
            label: 'Stopped in time',
            narrative: 'Three weeks off and a permanently smaller workload. Cheap, at the price.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'workload', delta: -20 },
              { k: 'burnout', op: 'load', delta: -24 },
              { k: 'stat', stat: 'health', delta: 6 },
              { k: 'stat', stat: 'energy', delta: 12 },
              { k: 'cashIncomeMonths', months: -0.6, note: 'unpaid leave' },
            ],
          },
        ],
      },
      {
        id: 'medicate',
        label: 'Manage the symptoms',
        hint: 'risky',
        tags: ['denial'],
        outcomes: [
          {
            weight: 1,
            label: 'Treated the symptoms',
            narrative: 'Effective, briefly. The cause continued uninterrupted.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -0.4, note: 'medication' },
              { k: 'stat', stat: 'health', delta: -6 },
              { k: 'burnout', op: 'load', delta: 10 },
              { k: 'schedule', afterYears: 3, label: 'The cause was still there', requires: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 60 }], effects: [{ k: 'burnout', op: 'load', delta: 16 }, { k: 'stat', stat: 'health', delta: -8 }] },
            ],
          },
        ],
      },
    ],
  },

  /* ---------------- career pivot pool ---------------- */
  {
    id: 'pivot.retrain',
    title: 'Retrain for something else',
    description: 'A real change of direction: two years of study, a drop in income, and no guarantees.',
    category: 'career',
    pools: ['pool.career.pivot'],
    weight: 7,
    cooldown: 10,
    requires: [{ k: 'age', min: 30, max: 58 }, { k: 'num', path: 'yearsExperience', op: '>=', value: 5 }],
    weightMods: [{ conditions: [{ k: 'num', path: 'meaningAlignment', op: '<=', value: 42 }], multiplier: 2 }],
    choices: [
      {
        id: 'commit',
        label: 'Do it properly',
        hint: 'slow',
        requires: [{ k: 'num', path: 'runwayMonths', op: '>=', value: 5 }],
        showWhenLocked: true,
        tags: ['change', 'growth', 'courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Went back to study',
            narrative: 'The youngest person in the room was nineteen. You were the most motivated.',
            tone: 'positive',
            effects: [
              { k: 'education', op: 'enroll', programId: 'edu.cert.pm' },
              { k: 'stat', stat: 'purpose', delta: 14 },
              { k: 'habit', habit: 'learning', delta: 14 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -8 },
            ],
          },
        ],
      },
      {
        id: 'sidestep',
        label: 'Move sideways instead',
        detail: 'A related role. Less disruption, less upside.',
        hint: 'safe',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Moved sideways',
            narrative: 'Adjacent work, familiar skills, a modest improvement in how Mondays feel.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 9 },
              { k: 'skill', skill: 'business', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay put',
        hint: 'safe',
        defers: { id: 'int.pivot', label: 'Change direction', topic: 'career', urgency: 26, decayEventId: 'career.pivotGone' },
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'You have a mortgage and a known quantity. Both are real arguments.',
            tone: 'neutral',
            effects: [{ k: 'inertia', op: 'pressure', delta: 5 }],
          },
        ],
      },
    ],
  },
  {
    id: 'pivot.goIndependent',
    title: 'Do the same work, for yourself',
    description: 'Your skills would sell directly. So would the risk, the admin, and the irregular income.',
    category: 'career',
    pools: ['pool.career.pivot', 'pool.business'],
    weight: 6,
    cooldown: 8,
    requires: [
      { k: 'num', path: 'yearsExperience', op: '>=', value: 6 },
      { k: 'careerStatus', anyOf: ['employed'] },
    ],
    choices: [
      {
        id: 'go',
        label: 'Go independent',
        hint: 'risky',
        tags: ['entrepreneurship', 'change'],
        outcomes: [
          {
            weight: 58,
            label: 'It worked',
            narrative: 'Fewer meetings, more control, and about the same money for two-thirds of the hours.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'careerReputation', op: '>=', value: 55 }], multiplier: 1.9 },
              { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 55 }], multiplier: 1.4 },
            ],
            effects: [
              { k: 'career', op: 'setStatus', status: 'selfEmployed' },
              { k: 'career', op: 'adjust', field: 'workload', delta: -8 },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 16 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -22 },
              { k: 'stat', stat: 'purpose', delta: 12 },
            ],
          },
          {
            weight: 42,
            label: 'The pipeline never filled',
            narrative: 'Two good clients, then four thin months, then a job application.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'setStatus', status: 'unemployed' },
              { k: 'cashIncomeMonths', months: -5, note: 'failed freelance attempt' },
              { k: 'stat', stat: 'stress', delta: 14 },
              { k: 'trait', trait: 'resilience', delta: 5 },
            ],
          },
        ],
      },
      {
        id: 'keep',
        label: 'Keep the salary',
        hint: 'safe',
        tags: ['security'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed employed',
            narrative: 'A predictable payslip is worth a great deal, and you were honest about that.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'stress', delta: -3 }],
          },
        ],
      },
    ],
  },

  /* ---------------- late career ---------------- */
  {
    id: 'late.youngerBoss',
    title: 'Your manager is thirty-one',
    description: 'Competent, energetic, and clearly unsure what to do with your twenty-eight years of experience.',
    category: 'career',
    pools: ['pool.career.late'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'age', min: 52 }, { k: 'careerStatus', anyOf: ['employed'] }],
    choices: [
      {
        id: 'support',
        label: 'Make them good',
        hint: 'safe',
        tags: ['contribution', 'legacy'],
        outcomes: [
          {
            weight: 1,
            label: 'Backed them',
            narrative: 'You made them look competent in rooms they were not in. It cost you nothing and they never forgot.',
            tone: 'positive',
            effects: [
              { k: 'career', op: 'adjust', field: 'reputation', delta: 9 },
              { k: 'counter', name: 'contributions', delta: 1 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'resent',
        label: 'Make the point about experience',
        hint: 'risky',
        tags: ['conflict'],
        outcomes: [
          {
            weight: 1,
            label: 'Became difficult',
            narrative: 'You were usually right and increasingly excluded.',
            tone: 'negative',
            effects: [
              { k: 'career', op: 'adjust', field: 'jobSecurity', delta: -16 },
              { k: 'relationshipAll', kind: 'colleague', dim: 'conflict', delta: 16 },
              { k: 'stat', stat: 'stress', delta: 9 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'late.consultancyOffer',
    title: 'They want to keep you, part-time',
    description: 'Three days a week, advisory, no line management. Less money and dramatically less nonsense.',
    category: 'career',
    pools: ['pool.career.late', 'pool.retirement'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'age', min: 56 }, { k: 'num', path: 'yearsExperience', op: '>=', value: 18 }],
    choices: [
      {
        id: 'take',
        label: 'Take the arrangement',
        hint: 'safe',
        tags: ['balance', 'retirement'],
        outcomes: [
          {
            weight: 1,
            label: 'Went part-time',
            narrative: 'The best years of the career turned out to be the last ones, at sixty per cent.',
            tone: 'positive',
            effects: [
              { k: 'salary', mode: 'mult', value: 0.65 },
              { k: 'career', op: 'adjust', field: 'workload', delta: -26 },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'stat', stat: 'energy', delta: 10 },
              { k: 'stat', stat: 'purpose', delta: 6 },
              { k: 'burnout', op: 'load', delta: -18 },
            ],
          },
        ],
      },
      {
        id: 'full',
        label: 'Stay full-time to the end',
        hint: 'mixed',
        tags: ['work', 'finance'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept going at full pace',
            narrative: 'Better for the pension, worse for the knees.',
            tone: 'neutral',
            effects: [
              { k: 'habit', habit: 'saving', delta: 8 },
              { k: 'counter', name: 'retirementBoost', delta: 1 },
              { k: 'burnout', op: 'load', delta: 8 },
              { k: 'stat', stat: 'health', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'late.ageism',
    title: 'The applications stop coming back',
    description: 'Twenty-nine years of experience and a search that has gone very quiet.',
    category: 'career',
    pools: ['pool.career.late', 'pool.crisis'],
    weight: 6,
    cooldown: 8,
    requires: [{ k: 'age', min: 54 }, { k: 'careerStatus', anyOf: ['unemployed', 'break'] }],
    choices: [
      {
        id: 'network',
        label: 'Work the network instead',
        hint: 'mixed',
        tags: ['social', 'pragmatic'],
        outcomes: [
          {
            weight: 62,
            label: 'Someone made a call',
            narrative: 'Not a job advert. A phone call from a person who remembered you.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 52 }], multiplier: 2.1 }],
            effects: [
              { k: 'career', op: 'setStatus', status: 'employed' },
              { k: 'career', op: 'adjust', field: 'meaningAlignment', delta: 6 },
              { k: 'relationshipAll', dim: 'trust', delta: 5 },
            ],
          },
          {
            weight: 38,
            label: 'The network had thinned',
            narrative: 'Most of the people who would have helped had retired.',
            tone: 'negative',
            effects: [
              { k: 'cashIncomeMonths', months: -6, note: 'extended unemployment' },
              { k: 'stat', stat: 'happiness', delta: -10 },
              { k: 'regret', op: 'add', id: 'regret.network', label: 'Let the professional network lapse', weight: 14 },
            ],
          },
        ],
      },
      {
        id: 'retire-early',
        label: 'Call it retirement',
        hint: 'mixed',
        tags: ['retirement', 'acceptance'],
        outcomes: [
          {
            weight: 1,
            label: 'Retired, not entirely by choice',
            narrative: 'You decided to call it a decision. That helped more than it should have.',
            tone: 'neutral',
            effects: [
              { k: 'career', op: 'retire' },
              { k: 'stat', stat: 'stress', delta: -12 },
              { k: 'stat', stat: 'purpose', delta: -14 },
              { k: 'triggerEvent', eventId: 'retirement.newStructure', afterYears: 1 },
            ],
          },
        ],
      },
    ],
  },

  /* ---------------- retirement ---------------- */
  {
    id: 'retirement.identity',
    title: 'Who are you now?',
    description: 'For forty years the answer began with a job title. It no longer does.',
    category: 'life',
    pools: ['pool.retirement', 'pool.retired.leisure'],
    weight: 7,
    cooldown: 6,
    requires: [{ k: 'careerStatus', anyOf: ['retired'] }],
    choices: [
      {
        id: 'rebuild',
        label: 'Build a new answer',
        hint: 'safe',
        tags: ['purpose', 'growth'],
        outcomes: [
          {
            weight: 1,
            label: 'Found a new one',
            narrative: 'Grandparent, volunteer, tolerable amateur cellist. All of it true.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'purpose', delta: 16 },
              { k: 'stat', stat: 'happiness', delta: 10 },
              { k: 'skill', skill: 'creative', delta: 5 },
              { k: 'counter', name: 'contributions', delta: 1 },
            ],
          },
        ],
      },
      {
        id: 'mourn',
        label: 'Miss the old one',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Kept looking backwards',
            narrative: 'You talked about work in the present tense for about three years.',
            tone: 'negative',
            effects: [
              { k: 'stat', stat: 'purpose', delta: -12 },
              { k: 'stat', stat: 'happiness', delta: -8 },
              { k: 'habit', habit: 'socialising', delta: -8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'retirement.downsize',
    title: 'The house is too big',
    description: 'Selling it would release money and end an era. Staying costs more every year.',
    category: 'finance',
    pools: ['pool.finance.retire', 'pool.retirement'],
    weight: 7,
    cooldown: 8,
    requires: [{ k: 'flag', flag: 'ownsHome' }, { k: 'age', min: 62 }],
    choices: [
      {
        id: 'sell',
        label: 'Downsize',
        hint: 'mixed',
        tags: ['finance', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Sold the family home',
            narrative: 'Capital released, rooms shed, and a surprising amount of grief about a kitchen.',
            tone: 'neutral',
            effects: [
              { k: 'property', op: 'sell' },
              { k: 'fixedExpenses', mode: 'mult', value: 0.62 },
              { k: 'stat', stat: 'happiness', delta: -6 },
              { k: 'stat', stat: 'stress', delta: -8 },
              { k: 'schedule', afterYears: 2, label: 'Settled into the smaller place', effects: [{ k: 'stat', stat: 'happiness', delta: 9 }] },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay in it',
        hint: 'costly',
        tags: ['roots'],
        outcomes: [
          {
            weight: 1,
            label: 'Kept the house',
            narrative: 'Everyone comes at Christmas. That is worth a number that does not appear on a statement.',
            tone: 'positive',
            effects: [
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 6 },
              { k: 'fixedExpenses', mode: 'add', value: 2600 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'retirement.pensionShortfall',
    title: 'The income does not reach',
    description: 'The arithmetic that looked adequate at sixty looks thin at seventy-two.',
    category: 'finance',
    pools: ['pool.finance.retire'],
    weight: 7,
    cooldown: 6,
    requires: [
      { k: 'careerStatus', anyOf: ['retired'] },
      { k: 'num', path: 'cashFlow', op: '<', value: 0 },
    ],
    choices: [
      {
        id: 'cut',
        label: 'Live smaller',
        hint: 'costly',
        tags: ['pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Cut back',
            narrative: 'Fewer trips, colder rooms, and a budget that finally balances.',
            tone: 'negative',
            effects: [
              { k: 'lifestyle', delta: -2 },
              { k: 'stat', stat: 'happiness', delta: -8 },
              { k: 'stat', stat: 'stress', delta: -4 },
            ],
          },
        ],
      },
      {
        id: 'work',
        label: 'Go back to some work',
        hint: 'mixed',
        requires: [{ k: 'age', max: 76 }],
        showWhenLocked: true,
        tags: ['work', 'pragmatic'],
        outcomes: [
          {
            weight: 1,
            label: 'Took a part-time job',
            narrative: 'Two mornings a week. Not the retirement in the brochure, and it worked.',
            tone: 'neutral',
            effects: [
              { k: 'otherIncome', mode: 'add', value: 11000 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'stat', stat: 'energy', delta: -6 },
            ],
          },
        ],
      },
      {
        id: 'family',
        label: 'Ask your family',
        hint: 'mixed',
        requires: [{ k: 'hasPerson', kind: 'child' }],
        showWhenLocked: true,
        tags: ['family'],
        outcomes: [
          {
            weight: 1,
            label: 'They helped',
            narrative: 'They were glad to. You found it much harder than they did.',
            tone: 'neutral',
            effects: [
              { k: 'otherIncome', mode: 'add', value: 8000 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 6 },
              { k: 'stat', stat: 'happiness', delta: -5 },
              { k: 'stat', stat: 'purpose', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'milestone.launchLeaving',
    title: 'Leaving the place you grew up',
    description: 'Somewhere else has more of what you want and none of what you know.',
    category: 'life',
    pools: ['pool.milestone.launch', 'pool.young'],
    ageMax: 26,
    weight: 8,
    unique: true,
    choices: [
      {
        id: 'go',
        label: 'Go',
        hint: 'mixed',
        tags: ['exploration', 'courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Left',
            narrative: 'A bigger place with more options and nobody who knew your history.',
            tone: 'neutral',
            effects: [
              { k: 'fixedExpenses', mode: 'mult', value: 1.28 },
              { k: 'trait', trait: 'adaptability', delta: 8 },
              { k: 'trait', trait: 'noveltySeeking', delta: 5 },
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: -8 },
              { k: 'counter', name: 'relocations', delta: 1 },
              { k: 'career', op: 'adjust', field: 'reputation', delta: 3 },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay',
        hint: 'safe',
        tags: ['roots', 'family'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'Cheaper, closer, and with a smaller set of possible futures.',
            tone: 'neutral',
            effects: [
              { k: 'relationshipAll', kind: 'parent', dim: 'closeness', delta: 10 },
              { k: 'relationshipAll', kind: 'friend', dim: 'closeness', delta: 8 },
              { k: 'habit', habit: 'saving', delta: 6 },
              { k: 'trait', trait: 'noveltySeeking', delta: -4 },
            ],
          },
        ],
      },
    ],
  },
];
